import axios from "axios";
import { API_BASE_URL } from "../config/env";
import { isSessionExpired, SESSION_EXPIRED_MESSAGE } from "./sessionErrors";
import {
  clearActiveTenant,
  getActiveTenant,
  isTenantDenied403,
  tenantSelectionRequiredError,
} from "../helpers/tenantHelpers";

const TENANT_OPTIONAL_PATHS = [
  "/accounts/auth/login/",
  "/accounts/auth/refresh/",
  "/accounts/auth/switch-tenant/",
  "/accounts/auth/tenant-signup/",
  "/accounts/tenant-signup/",
  "/accounts/auth/forgot-password/",
  "/accounts/auth/verify-reset-code/",
  "/accounts/auth/reset-password/",
  "/accounts/auth/accept-invite/",
];

const requiresTenant = (url = "") =>
  !TENANT_OPTIONAL_PATHS.some((path) => String(url || "").includes(path));

export const normalizeArray = (value) => {
  if (Array.isArray(value)) return value;
  const candidate = value || {};
  const keys = [
    "results",
    "data",
    "items",
    "rows",
    "threads",
    "messages",
    "calls",
    "campaigns",
    "customers",
    "leads",
  ];
  for (const key of keys) {
    if (Array.isArray(candidate?.[key])) return candidate[key];
  }
  return [];
};

export const toApiError = (error, fallbackMessage = "Request failed") => {
  const status = Number(error?.response?.status || 0) || null;
  const payload = error?.response?.data || {};
  const message =
    payload?.detail ||
    payload?.message ||
    payload?.error ||
    error?.message ||
    fallbackMessage;
  const normalized = new Error(String(message));
  normalized.status = status;
  normalized.code =
    error?.code ||
    payload?.code ||
    (status === 401 ? "UNAUTHORIZED" : status === 403 ? "FORBIDDEN" : "API_ERROR");
  normalized.payload = payload;
  normalized.original = error;
  return normalized;
};

// Session-expired detection lives in a dependency-free module (jest can import
// it without axios); re-exported here so existing imports keep working.
export { isSessionExpired, SESSION_EXPIRED_MESSAGE };

// One place that turns an API error into the words a page shows. Callers pass
// their own texts for the states they know (409 conflict, real 403); the
// session case wins over all of them.
export const describeApiError = (err, { conflict, forbidden, fallback } = {}) => {
  if (isSessionExpired(err)) return SESSION_EXPIRED_MESSAGE;
  const status = Number(err?.status || 0);
  const detail = err?.payload?.detail;
  if (status === 409) return conflict || detail || "That's no longer pending.";
  if (status === 403) return forbidden || detail || "You don't have permission to do that.";
  return detail || fallback || "Request failed.";
};

export const withLoading = async (setLoading, task) => {
  if (typeof setLoading === "function") setLoading(true);
  try {
    return await task();
  } finally {
    if (typeof setLoading === "function") setLoading(false);
  }
};

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 20000,
});

apiClient.interceptors.request.use(
  (config) => {
    const tenant = getActiveTenant();
    const token =
      localStorage.getItem("token") || localStorage.getItem("access_token");
    const needsTenant = requiresTenant(config.url);

    config.headers = config.headers || {};
    if (token) config.headers.Authorization = `Bearer ${token}`;

    if (needsTenant && !tenant?.id) {
      return Promise.reject(tenantSelectionRequiredError());
    }

    if (tenant?.id) config.headers["X-Tenant-ID"] = tenant.id;
    if (tenant?.domain) config.headers["X-Tenant-Domain"] = tenant.domain;
    if (tenant?.industry) config.headers["X-Tenant-Industry"] = tenant.industry;
    return config;
  },
  (error) => Promise.reject(toApiError(error))
);

let isRefreshing = false;
let refreshQueue = [];

// Decode a JWT's exp claim → ISO string, so token_expiry always tracks the real
// access token (the app-boot guard reads token_expiry to decide if the session
// is alive). Without this, a refreshed token kept a STALE expiry and the next
// reload logged the user out despite a valid session.
const jwtExpiryIso = (token) => {
  try {
    const payload = String(token).split(".")[1];
    const json = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return json && json.exp ? new Date(json.exp * 1000).toISOString() : null;
  } catch {
    return null;
  }
};

// Persist a fresh access token AND its expiry together.
export const storeAccessToken = (token) => {
  localStorage.setItem("token", token);
  localStorage.setItem("access_token", token);
  const exp = jwtExpiryIso(token);
  if (exp) localStorage.setItem("token_expiry", exp);
};

// Exchange the refresh token for a new access token. Shared by the 401
// interceptor and the app-boot silent refresh. Throws when there's no refresh
// token or the exchange fails — the caller decides whether to log out.
export const refreshAccessToken = async () => {
  const refresh = localStorage.getItem("refresh");
  if (!refresh) throw new Error("No refresh token");
  const res = await apiClient.post("/accounts/auth/refresh/", { refresh });
  const nextAccess = String(res?.data?.access || "").trim();
  if (!nextAccess) throw new Error("Missing refreshed access token");
  storeAccessToken(nextAccess);
  // The refresh payload carries the user (incl. is_superuser); merge it into the
  // stored user so platform gating works without forcing a re-login.
  if (res?.data?.user && typeof res.data.user === "object") {
    try {
      const prev = JSON.parse(localStorage.getItem("user") || "{}");
      localStorage.setItem("user", JSON.stringify({ ...prev, ...res.data.user }));
    } catch { /* non-fatal */ }
  }
  return nextAccess;
};

const IDEMPOTENT_METHODS = new Set(["get", "head", "options"]);
const isIdempotent = (config) =>
  IDEMPOTENT_METHODS.has(String(config?.method || "get").toLowerCase());

const flushRefreshQueue = (nextAccess, refreshError) => {
  refreshQueue.forEach(({ resolve, reject, config }) => {
    if (refreshError) {
      reject(refreshError);
      return;
    }
    // Only auto-retry idempotent requests after token refresh.
    // Non-idempotent requests (POST, PATCH, PUT, DELETE) must not be
    // replayed — they could create duplicate records.
    if (!isIdempotent(config)) {
      reject(toApiError(
        { message: "Session expired. Please retry your action." },
        "Session expired"
      ));
      return;
    }
    const tenant = getActiveTenant();
    config.headers = config.headers || {};
    if (nextAccess) config.headers.Authorization = `Bearer ${nextAccess}`;
    if (tenant?.id) config.headers["X-Tenant-ID"] = tenant.id;
    resolve(apiClient(config));
  });
  refreshQueue = [];
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const requestConfig = error?.config || {};
    const requestUrl = String(requestConfig.url || "");

    // The tenant middleware answers expired/invalid tokens with 403
    // "Authentication required" — NOT 401. Treating every 403 as tenant
    // denial nuked tenant context and bounced users to account selection
    // on ordinary token expiry (the week-long "click nav → select account"
    // bug). Auth-shaped 403s take the refresh path like a 401.
    const responseDetail = String(error?.response?.data?.detail || "");
    const authShaped403 =
      status === 403 && /authentication required|token not valid|token is invalid|token_not_valid/i.test(responseDetail);

    // Clear the tenant + bounce ONLY on a genuine tenant-denial 403. A plain
    // per-object/capability 403 now rejects to the caller (which .catch()es it)
    // without nuking the whole tenant session; auth-shaped 403s fall through to
    // the refresh path below.
    if (isTenantDenied403(status, error?.response?.data)) {
      clearActiveTenant();
      if (typeof window !== "undefined") window.location.href = "/select-account";
      return Promise.reject(toApiError(error, "Tenant access denied"));
    }

    if ((status !== 401 && !authShaped403) || requestConfig._retry) {
      return Promise.reject(toApiError(error));
    }

    if (requestUrl.includes("/accounts/auth/refresh/")) {
      localStorage.clear();
      return Promise.reject(toApiError(error, "Session expired"));
    }

    requestConfig._retry = true;
    const refresh = localStorage.getItem("refresh");
    if (!refresh) {
      localStorage.clear();
      return Promise.reject(toApiError(error, "Session expired"));
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        refreshQueue.push({ resolve, reject, config: requestConfig });
      });
    }

    isRefreshing = true;
    try {
      const refreshResponse = await apiClient.post("/accounts/auth/refresh/", {
        refresh,
      });
      const nextAccess = String(refreshResponse?.data?.access || "").trim();
      if (!nextAccess) throw new Error("Missing refreshed access token");

      storeAccessToken(nextAccess); // token + access_token + token_expiry
      flushRefreshQueue(nextAccess, null);

      // Only auto-retry the original request if it's idempotent.
      if (!isIdempotent(requestConfig)) {
        return Promise.reject(
          toApiError(
            { message: "Session refreshed. Please retry your action." },
            "Session expired"
          )
        );
      }
      requestConfig.headers = requestConfig.headers || {};
      requestConfig.headers.Authorization = `Bearer ${nextAccess}`;
      return apiClient(requestConfig);
    } catch (refreshError) {
      flushRefreshQueue("", refreshError);
      localStorage.clear();
      if (typeof window !== "undefined") window.location.href = "/login";
      return Promise.reject(toApiError(refreshError, "Session expired"));
    } finally {
      isRefreshing = false;
    }
  }
);

export default apiClient;
